import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, MapPin, Plus, Check, AlertCircle, Wallet } from 'lucide-react';
import { SideMenuModal } from './SideMenuModal';
import { colors } from '../constants/colors';
import { getPaymentMethods, getAddresses, getBalanceSummary } from '../api/client';
import type { PaymentMethod, BuyerAddress } from '../types';

interface Props {
  onClose: () => void;
  useBalance?: boolean;
  onUseBalanceChange?: (value: boolean) => void;
}

const CARD_BRANDS: Record<string, { color: string; name: string }> = {
  visa: { color: '#1A1F71', name: 'Visa' },
  mastercard: { color: '#EB001B', name: 'Mastercard' },
  amex: { color: '#006FCF', name: 'Amex' },
  discover: { color: '#FF6000', name: 'Discover' },
};

function brandMeta(brand: string) {
  return CARD_BRANDS[brand?.toLowerCase()] || { color: colors.gray[600], name: 'Card' };
}

export function WalletModal({ onClose, useBalance: useBalanceProp, onUseBalanceChange }: Props) {
  const navigate = useNavigate();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [addresses, setAddresses] = useState<BuyerAddress[]>([]);
  const [balance, setBalance] = useState<{ availableBalance: number } | null>(null);
  const [localUseBalance, setLocalUseBalance] = useState(true);
  const useBalance = useBalanceProp ?? localUseBalance;
  const setUseBalance = (v: boolean) => { setLocalUseBalance(v); onUseBalanceChange?.(v); };
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getPaymentMethods().catch(() => []),
      getAddresses().catch(() => []),
      getBalanceSummary().catch(() => null),
    ])
      .then(([pm, addr, bal]) => {
        if (cancelled) return;
        setMethods(pm);
        setAddresses(addr);
        setBalance(bal);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const defaultCard = methods.find(m => m.isDefault) || methods[0] || null;
  const defaultAddr = addresses.find(a => a.isDefault) || addresses[0] || null;
  const hasCard = methods.length > 0;
  const hasAddr = addresses.length > 0;

  return (
    <SideMenuModal title="Wallet" onClose={onClose}>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.gray[600] }}>Loading...</div>
      ) : (
        <>
          {/* Payment Method */}
          <Section
            label="Payment Method"
            action={hasCard ? { label: 'Manage', onClick: () => navigate('/payment-methods') } : null}
          >
            {hasCard && defaultCard ? (
              <button
                onClick={() => navigate('/payment-methods')}
                style={rowBtn}
              >
                <div style={{
                  width: 44, height: 32, borderRadius: 6,
                  background: brandMeta(defaultCard.cardBrand).color + '20',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginRight: 12, flexShrink: 0,
                }}>
                  <CreditCard size={18} color={brandMeta(defaultCard.cardBrand).color} />
                </div>
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: colors.ink.black, fontSize: 14 }}>
                    {brandMeta(defaultCard.cardBrand).name} ••••{defaultCard.cardLast4}
                  </div>
                  <div style={{ fontSize: 12, color: colors.gray[600], marginTop: 2 }}>
                    Expires {String(defaultCard.cardExpMonth).padStart(2, '0')}/{String(defaultCard.cardExpYear).slice(-2)}
                  </div>
                </div>
                <ReadyPill />
              </button>
            ) : (
              <EmptyCTA
                title="No payment method"
                subtitle="Add a card to place bids"
                onClick={() => navigate('/payment-methods')}
              />
            )}
          </Section>

          {/* Snagg Balance (within Payment Method section) */}
          {balance !== null && (
            <div style={{
              display: 'flex', alignItems: 'center',
              background: balance.availableBalance > 0 ? colors.gray[100] : colors.gray[100],
              borderRadius: 16, padding: 14, marginBottom: 20,
              opacity: balance.availableBalance > 0 ? 1 : 0.45,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: 'rgba(95,61,196,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: 12, flexShrink: 0,
              }}>
                <Wallet size={20} color="#5F3DC4" />
              </div>
              <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: colors.ink.black, fontSize: 14 }}>
                  Snagg Balance
                </div>
                <div style={{ fontSize: 12, color: colors.gray[600], marginTop: 2 }}>
                  {balance.availableBalance > 0
                    ? `$${balance.availableBalance.toFixed(2)} available · applied first, remainder to card`
                    : '$0.00 — earn balance from promos, refunds, and sales'}
                </div>
              </div>
              {balance.availableBalance > 0 && (
                <button
                  onClick={() => setUseBalance(!useBalance)}
                  style={{
                    width: 36, height: 20, borderRadius: 10, border: 'none',
                    background: useBalance ? '#5F3DC4' : '#E5E5EA',
                    position: 'relative', cursor: 'pointer', flexShrink: 0,
                    transition: 'background 200ms',
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff', position: 'absolute', top: 2,
                    left: useBalance ? 18 : 2,
                    transition: 'left 200ms',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }} />
                </button>
              )}
            </div>
          )}

          {/* Shipping Address */}
          <Section
            label="Shipping Address"
            action={hasAddr ? { label: 'Manage', onClick: () => navigate('/addresses') } : null}
          >
            {hasAddr && defaultAddr ? (
              <button
                onClick={() => navigate('/addresses')}
                style={rowBtn}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: colors.gray[300],
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginRight: 12, flexShrink: 0,
                }}>
                  <MapPin size={20} color={colors.ink.black} />
                </div>
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: colors.ink.black, fontSize: 14 }}>
                    {defaultAddr.name}
                  </div>
                  <div style={{
                    fontSize: 12, color: colors.gray[600], marginTop: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {defaultAddr.streetLine1}, {defaultAddr.city}, {defaultAddr.state}
                  </div>
                </div>
                <ReadyPill />
              </button>
            ) : (
              <EmptyCTA
                title="No shipping address"
                subtitle="Add an address to receive items"
                onClick={() => navigate('/addresses')}
              />
            )}
          </Section>

          {hasCard && hasAddr && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#22C55E10', borderRadius: 12, padding: '12px 16px', gap: 8,
            }}>
              <Check size={18} color="#22C55E" />
              <span style={{ color: '#22C55E', fontWeight: 600, fontSize: 14 }}>You're ready to bid!</span>
            </div>
          )}
        </>
      )}
    </SideMenuModal>
  );
}

function Section({ label, action, children }: { label: string; action: { label: string; onClick: () => void } | null; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{
          fontSize: 12, fontWeight: 600, color: colors.gray[600],
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>{label}</span>
        {action && (
          <button onClick={action.onClick} style={{ background: 'none', border: 'none', color: colors.brand.blue, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            {action.label}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function ReadyPill() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      background: '#22C55E20', color: '#22C55E',
      padding: '4px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600, flexShrink: 0,
    }}>
      <Check size={12} /> Ready
    </div>
  );
}

function EmptyCTA({ title, subtitle, onClick }: { title: string; subtitle: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', width: '100%',
        background: '#FEF3C7', border: '1px solid #F59E0B',
        borderRadius: 16, padding: 14, cursor: 'pointer',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 22,
        background: '#F59E0B20',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginRight: 12, flexShrink: 0,
      }}>
        <AlertCircle size={22} color="#F59E0B" />
      </div>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <div style={{ fontWeight: 600, color: '#92400E', fontSize: 14 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>{subtitle}</div>
      </div>
      <div style={{
        width: 32, height: 32, borderRadius: 16,
        background: colors.brand.blue,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', flexShrink: 0,
      }}>
        <Plus size={18} />
      </div>
    </button>
  );
}

const rowBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  background: colors.gray[100],
  border: 'none',
  borderRadius: 16,
  padding: 14,
  cursor: 'pointer',
};
